import { type ButtonHTMLAttributes, type ReactNode } from 'react';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  children: ReactNode;
}

const styles = {
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    fontFamily: 'var(--font)',
    fontWeight: 'var(--font-medium)' as const,
    borderRadius: 'var(--radius-md)',
    transition: 'var(--transition-fast)',
    cursor: 'pointer',
    border: 'none',
    textDecoration: 'none',
    whiteSpace: 'nowrap' as const,
  },
  sizes: {
    sm: { padding: '6px 12px', fontSize: 'var(--text-sm)' },
    md: { padding: '10px 18px', fontSize: 'var(--text-sm)' },
    lg: { padding: '13px 24px', fontSize: 'var(--text-base)' },
  },
  variants: {
    primary: {
      background: 'var(--orange)',
      color: 'var(--white)',
    },
    secondary: {
      background: 'var(--white)',
      color: 'var(--text-primary)',
      border: '1px solid var(--border)',
      boxShadow: 'var(--shadow-sm)',
    },
    ghost: {
      background: 'transparent',
      color: 'var(--text-secondary)',
    },
    danger: {
      background: 'var(--error)',
      color: 'var(--white)',
    },
  },
} as const;

export function Button({
  variant = 'primary',
  size = 'md',
  loading,
  disabled,
  children,
  style,
  className,
  ...props
}: ButtonProps) {
  return (
    <button
      className={className ? `touch-target ${className}` : 'touch-target'}
      disabled={disabled || loading}
      style={{
        ...styles.base,
        ...styles.sizes[size],
        ...styles.variants[variant],
        opacity: disabled || loading ? 0.6 : 1,
        cursor: disabled || loading ? 'not-allowed' : 'pointer',
        ...style,
      }}
      {...props}
    >
      {loading && (
        <span style={{ width: 14, height: 14, border: '2px solid currentColor', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.6s linear infinite', display: 'inline-block' }} />
      )}
      {children}
    </button>
  );
}
